import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Button, Radio, Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';

import ViewPanel from 'vcomponents/Form/ViewPanel';
import FormBuilder from 'vcomponents/FormBuilder/index2';
import { withModalContext } from 'context/ModalContext';

import QuickOrderPriceModal from './components/QuickOrderPriceModal';
import { PICKUP_LOADING_COLUMN, DROPOFF_LOADING_COLUMN, QUICK_GOODS_COLUMN, QUICK_ETC_COLUMN } from './viewMeta';
import { toSendAppointmentDate } from './utils';
import { useAddressBookList, useCheckQuickOrderPrice } from '../hooks';
import { IQuickAddress, IQuickBookingParams, IQuickOrderAddress, TVehicle } from '../types';

import NaverAddressForm from './components/NaverAddressForm';
import { GoodsWrapper, RadioGroupWrapper, LoadingWrapper } from './styles';

interface vehcileOption {
  label: string;
  value: TVehicle;
}

const HELPER_OPTION = {
  XS: '물품 크기 : 가로 + 세로 + 높이의 합 80 cm / 무게 : 2 kg 이하',
  S: '가로 + 세로 + 높이의 합 100 cm / 무게 5 kg 이하',
  M: '가로 + 세로 + 높이의 합 140 cm / 무게 20 kg 이하',
  L: '가로 + 세로 + 높이의 합 140 cm초과, 무게 20 kg 초과'
};
const VEHICLE_HELPER_OPTION = {
  MOTORCYCLE: '퀵 요금이 적용되며 할인율에 따른 할인금액이 차감 됩니다',
  JIMBAJI_MOTORCYCLE: '퀵 요금이 적용되며 할인율에 따른 할인금액이 차감 됩니다',
  PASSENGER_CAR: '퀵 요금이 적용되며 할인율에 따른 할인금액이 차감 됩니다',
  DAMAS: '화물 요금이 적용되며 화물 이용료가 추가 적용 됩니다',
  LABO: '화물 요금이 적용되며 화물 이용료가 추가 적용 됩니다',
  TON: '화물 요금이 적용되며 화물 이용료가 추가 적용 됩니다'
};
const GOODS_TYPE_OPTIONS = [
  { label: '서류 / 초소형', value: 'XS' },
  { label: '소형', value: 'S' },
  { label: '중형', value: 'M' },
  { label: '대형', value: 'L' }
];
const VEHICLE_OPTIONS: vehcileOption[] = [
  { label: '오토바이', value: 'MOTORCYCLE' },
  { label: '짐받이 오토바이', value: 'JIMBAJI_MOTORCYCLE' },
  { label: '승용차', value: 'PASSENGER_CAR' },
  { label: '다마스', value: 'DAMAS' },
  { label: '라보', value: 'LABO' },
  { label: '1톤', value: 'TON' }
];

const VEHICLE_CHECK: vehcileOption[] = [
  { label: '오토바이', value: 'MOTORCYCLE' },
  { label: '짐받이 오토바이', value: 'JIMBAJI_MOTORCYCLE' },
  { label: '승용차', value: 'PASSENGER_CAR' },
  { label: '다마스', value: 'DAMAS' },
  { label: '라보', value: 'LABO' },
  { label: '1톤', value: 'TON' },
  { label: '1톤 트럭', value: 'TON' }
];

const getVehicle = (type: string): Record<string, string>[] => {
  const target = type === 'pickup' ? '발송인' : '수령인';
  const loading = type === 'pickup' ? '상차' : '하차';
  return [
    {
      type: 'USER',
      label: `고객이 ${loading}`,
      description: '',
      example: `${target}이 직접\n 물품운반과 차량${loading} 추가요금 없음`
    },
    {
      type: 'TOGETHER',
      label: `같이 ${loading}`,
      description: '',
      example: '추가요금 : 다마스 : +3,000원 | 라보 : +4,000원 | 1톤 : 8,000원'
    },
    {
      type: 'PICKER',
      label: `기사님이 ${loading}`,
      description: '',
      example: '추가요금 : 다마스 : +6,000원 | 라보 : +8,000원 | 1톤 : 16,000원'
    }
  ];
};

export const RadioButton = ({ type, label, description, example }: Record<string, string>) => (
  <Radio.Button value={type}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div
        style={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column', gap: '2px', lineHeight: '19px' }}
      >
        <div className="goods-name">{label}</div>
        <div className="goods-ex">{example}</div>
      </div>
      <div className="size-description">{description}</div>
    </div>
  </Radio.Button>
);

// 화면 진입 시점 기준으로 계산한다 — 모듈 로드 시점에 고정하면 재진입 시 기본값이 과거 시각이 된다
const getInitValue = (): IQuickBookingParams => ({
  orderType: 'QUICK',
  goodsType: 'XS',
  vehicle: undefined,
  appointmentDate: moment().add(5, 'minute')
});

interface QuickReservationProps {
  context: any;
  history: any;
}
function QuickReservation({ context, history }: QuickReservationProps) {
  const [params, setParams] = useState<IQuickBookingParams>(getInitValue);
  const [pickupParams, setPickupParams] = useState<IQuickOrderAddress>({
    isSaveAddress: false
  } as IQuickOrderAddress);
  const [dropoffParams, setDropoffParams] = useState<IQuickOrderAddress>({
    isSaveAddress: false
  } as IQuickOrderAddress);

  const [quickOrderPrice, setQuickOrderPrice] = useState<number>();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const { data } = useAddressBookList(true);
  const { mutateAsync: checkQuickOrderPrice } = useCheckQuickOrderPrice();

  const handleOnOffModal = () => setIsModalOpen(!isModalOpen);
  const handleValueChange = (key: string, value: any) => {
    if (key === 'goodsType') {
      const subMiddleVehicle =
        params?.vehicle === 'MOTORCYCLE' ||
        params?.vehicle === 'JIMBAJI_MOTORCYCLE' ||
        params?.vehicle === 'PASSENGER_CAR';
      const vehicle = value !== 'L' && subMiddleVehicle ? params?.vehicle : undefined;
      setParams((prevState) => ({ ...prevState, vehicle, [key]: value }));
    } else {
      setParams((prevState) => ({ ...prevState, [key]: value }));
    }
  };

  const handlePickupChange = (key: string, value: any) => {
    setPickupParams((prevState) => ({ ...prevState, [key]: value }));
  };
  const handleDropoffChange = (key: string, value: any) => {
    setDropoffParams((prevState) => ({ ...prevState, [key]: value }));
  };

  const formatAddress = (address?: IQuickOrderAddress): IQuickOrderAddress => ({
    ...address,
    addressBookName: address?.addressBookName || address?.name
  });

  const handleSubmit = async () => {
    if (params.goodsType === 'L' && (!pickupParams.loadingMethod || !dropoffParams.loadingMethod)) {
      Modal.confirm({
        title: '경고',
        content: '배송 운반 지원을 선택해주세요.',
        icon: <ExclamationCircleOutlined />,
        okText: '확인',
        cancelButtonProps: { style: { display: 'none' } }
      });
      return;
    }
    const price = await checkQuickOrderPrice({
      ...params,
      appointmentDate: toSendAppointmentDate(params?.appointmentDate),
      pickup: formatAddress(pickupParams),
      dropoff: formatAddress(dropoffParams)
    });
    setQuickOrderPrice(price.totalPrice);
    handleOnOffModal();
  };

  const loadingMethodReturn = (description: string | undefined | null): string | undefined => {
    if (!description) {
      return undefined;
    }
    if (description.includes('고객')) {
      return 'USER';
    } else if (description.includes('같이')) {
      return 'TOGETHER';
    } else if (description.includes('기사')) {
      return 'PICKER';
    }
    return undefined;
  };

  const reapplyAddressData = (quickAddressData: IQuickAddress): IQuickOrderAddress => {
    if (!quickAddressData.gpslat || !quickAddressData.gpslon) {
      window.naver.maps.Service.geocode(
        { query: quickAddressData?.roadAddress || quickAddressData?.jibunAddress },
        (state: number, response: any) => {
          if (state === window.naver.maps.Service.Status.OK) {
            const result = response.v2.addresses[0];
            return {
              userSelectedType: quickAddressData.userSelectedType,
              zonecode: quickAddressData.zoneCode,
              roadAddress: quickAddressData.roadAddress,
              jibunAddress: quickAddressData.jibunAddress,
              addressDetail: quickAddressData.addressDetail,
              gpslat: result.y,
              gpslon: result.x,
              name: quickAddressData.name,
              cellphone: quickAddressData.cellphone,
              isSaveAddress: false,
              addressBookName: '',
              description: quickAddressData?.description,
              loadingMethod: loadingMethodReturn(quickAddressData.loadingMethodDescription)
            };
          }
        }
      );
    } else {
      return {
        userSelectedType: quickAddressData.userSelectedType,
        zonecode: quickAddressData.zoneCode,
        roadAddress: quickAddressData.roadAddress,
        jibunAddress: quickAddressData.jibunAddress,
        addressDetail: quickAddressData.addressDetail,
        gpslat: quickAddressData.gpslat,
        gpslon: quickAddressData.gpslon,
        name: quickAddressData.name,
        cellphone: quickAddressData.cellphone,
        isSaveAddress: false,
        addressBookName: '',
        description: quickAddressData.description,
        loadingMethod: loadingMethodReturn(quickAddressData.loadingMethodDescription)
      };
    }
  };

  useEffect(() => {
    if (history.location.data) {
      const quickData = history.location.data;
      const reApplyParams: IQuickBookingParams = {
        orderType: 'QUICK',
        goodsType: quickData.quickGoodsType,
        appointmentDate: moment().add(30 - (moment().minute() % 10), 'minute'),
        vehicle: VEHICLE_CHECK.find((el) => el.label === quickData.vehicle)?.value,
        ...(quickData.goodsInfo != null && { goodsInfo: quickData.goodsInfo }),
        ...(quickData.remark !== '-' && { remark: quickData.remark })
      };

      if (quickData.quickAddressList?.length === 2) {
        const pickup = quickData.quickAddressList[0];
        const dropoff = quickData.quickAddressList[1];
        setPickupParams(reapplyAddressData(pickup));
        setDropoffParams(reapplyAddressData(dropoff));
      }

      setParams(reApplyParams);
    }
  }, [data]);

  return (
    <ViewPanel title="퀵 예약하기">
      <ViewPanel title="기본 정보" theme="none">
        <GoodsWrapper>
          <FormBuilder columns={QUICK_GOODS_COLUMN} data={params} onValuesChange={handleValueChange}>
            <div id="goodsType">
              <Radio.Group
                value={params?.goodsType}
                onChange={(event) => handleValueChange('goodsType', event.target.value)}
              >
                {GOODS_TYPE_OPTIONS.map(({ label, value }) => (
                  <Radio.Button key={value} value={value}>
                    {label}
                  </Radio.Button>
                ))}
              </Radio.Group>
              <div className="helperText">{HELPER_OPTION[params?.goodsType]}</div>
            </div>
            <div id="vehicle">
              <Radio.Group
                value={params?.vehicle}
                onChange={({ target }) => handleValueChange('vehicle', target.value)}
              >
                {VEHICLE_OPTIONS.map(({ label, value }) => {
                  const subMiddleVehicle =
                    value === 'MOTORCYCLE' || value === 'JIMBAJI_MOTORCYCLE' || value === 'PASSENGER_CAR';
                  const largeOrlagerVehicle = value === 'DAMAS' || value === 'LABO' || value === 'TON';

                  const isMotorcycle = params?.goodsType !== 'L' && subMiddleVehicle;
                  const disabled = isMotorcycle || (params?.goodsType === 'L' && largeOrlagerVehicle);
                  return (
                    <Radio.Button key={value} value={value} disabled={!disabled}>
                      {label}
                    </Radio.Button>
                  );
                })}
              </Radio.Group>
              <div className="helperText">{params?.vehicle ? VEHICLE_HELPER_OPTION[params?.vehicle] : '.'}</div>
            </div>
          </FormBuilder>
        </GoodsWrapper>
      </ViewPanel>
      <ViewPanel title="주소 정보" theme="none">
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          {/* pickup */}
          <NaverAddressForm
            type="pickup"
            context={context}
            selectedAddress={pickupParams}
            addressBookList={data?.content}
            onChange={setPickupParams}
          />
          {/* dropoff */}
          <NaverAddressForm
            type="dropoff"
            context={context}
            selectedAddress={dropoffParams}
            addressBookList={data?.content}
            onChange={setDropoffParams}
          />
        </div>
        {params?.goodsType === 'L' ? (
          <LoadingWrapper>
            <ViewPanel title="상차 물품 운반 신청" theme="none" style={{ width: '100%' }}>
              <FormBuilder columns={PICKUP_LOADING_COLUMN} data={pickupParams} onValuesChange={handlePickupChange}>
                <RadioGroupWrapper
                  id="pickup"
                  value={pickupParams?.loadingMethod}
                  onChange={({ target }) => handlePickupChange('loadingMethod', target.value)}
                >
                  {getVehicle('pickup').map((item) => (
                    <RadioButton key={`pickup-${item.type}`} {...item} />
                  ))}
                </RadioGroupWrapper>
              </FormBuilder>
            </ViewPanel>
            <ViewPanel title="하차 물품 운반 신청" theme="none" style={{ width: '100%' }}>
              <FormBuilder columns={DROPOFF_LOADING_COLUMN} data={dropoffParams} onValuesChange={handleDropoffChange}>
                <RadioGroupWrapper
                  id="dropoff"
                  value={dropoffParams?.loadingMethod}
                  onChange={({ target }) => handleDropoffChange('loadingMethod', target.value)}
                >
                  {getVehicle('dropoff').map((item) => (
                    <RadioButton key={`dropoff-${item.type}`} {...item} />
                  ))}
                </RadioGroupWrapper>
              </FormBuilder>
            </ViewPanel>
          </LoadingWrapper>
        ) : null}
      </ViewPanel>
      <ViewPanel title="부가 정보" theme="none">
        <div style={{ width: '50%' }}>
          <FormBuilder columns={QUICK_ETC_COLUMN} data={params} onValuesChange={handleValueChange} />
        </div>
      </ViewPanel>

      {/* footer */}
      <div style={{ display: 'flex', gap: '8px', float: 'right' }}>
        <Button onClick={() => history.push('/service/quick/reservation/list')}>취소</Button>
        <Button type="primary" onClick={handleSubmit}>
          예상 운임 확인
        </Button>
      </div>
      <QuickOrderPriceModal
        open={isModalOpen}
        close={handleOnOffModal}
        quickData={{
          ...params,
          pickup: formatAddress(pickupParams),
          dropoff: formatAddress(dropoffParams)
        }}
        quickOrderPrice={quickOrderPrice}
        history={history}
      />
    </ViewPanel>
  );
}
export default withModalContext(QuickReservation);
